// src/pages/RoomStatusPage.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Dropdown, Input, Modal, Table } from "antd";
import { useAuth } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance";
import {
  CardTitle,
  ListToolbar,
  SectionCard,
  StatusTag,
  TabBar,
  fmtDate,
  tablePagination,
  useFlatPage,
} from "../components/ui";
import { IconDown } from "../components/ui/icons";
import notify from "../utils/notify";

const STATUS_TO_ENUM = {
  Available: 0,
  Occupied: 1,
  Maintenance: 2,
  Reserved: 3,
  UnderMaintenance: 4,
  Cleaning: 5,
  Dirty: 6,
  OutOfService: 7,
  Unknown: 9,
};

const ENUM_TO_STATUS = {
  0: "Available",
  1: "Occupied",
  2: "Maintenance",
  3: "Reserved",
  4: "UnderMaintenance",
  5: "Cleaning",
  6: "Dirty",
  7: "OutOfService",
  9: "Unknown",
};

const pickApiMessage = (err, fallback) => {
  const d = err?.response?.data;
  return d?.message || d?.Message || d?.error || d?.Error || fallback;
};

const RoomStatusPage = () => {
  useFlatPage();
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusTab, setStatusTab] = useState("all");
  const [error, setError] = useState(null);

  // maintenance/out-of-service reason modal
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [newStatus, setNewStatus] = useState(null);
  const [maintenanceReason, setMaintenanceReason] = useState("");
  const [updating, setUpdating] = useState(false);

  const normalizeStatus = useCallback((status) => {
    // Accept: enum int, numeric string, or name string
    if (typeof status === "number" && ENUM_TO_STATUS[status]) return ENUM_TO_STATUS[status];

    const sRaw = status ?? "";
    const s = String(sRaw).trim();

    // numeric string like "6"
    const n = Number(s);
    if (!Number.isNaN(n) && ENUM_TO_STATUS[n]) return ENUM_TO_STATUS[n];

    // synonyms / legacy
    const statusMap = {
      UnderMaintenance: "Maintenance",
      OutOfOrder: "OutOfService",
      Unavailable: "OutOfService",
      "": "Unknown",
      null: "Unknown",
      undefined: "Unknown",
    };

    return statusMap[s] || s;
  }, []);

  const getAvailableStatusTransitions = useCallback((currentStatus) => {
    const transitions = {
      Available: ["Occupied", "Reserved", "Maintenance", "Dirty"],
      Occupied: ["Cleaning", "Dirty"],
      Cleaning: ["Available", "Dirty"],
      Reserved: ["Occupied", "Available"],
      Maintenance: ["Available", "OutOfService"],
      Dirty: ["Cleaning", "OutOfService"],
      OutOfService: ["Maintenance", "Available"],
      Unknown: ["Available"],
    };
    return transitions[currentStatus] || [];
  }, []);

  const fetchRoomStatuses = useCallback(async () => {
    if (!hotelId) return;

    try {
      setLoading(true);
      const res = await axiosInstance.get(`/RoomStatus/Hotel/${hotelId}`);
      const list = Array.isArray(res.data) ? res.data : [];
      setRooms(list);
      setError(null);
    } catch (err) {
      console.error("Error fetching room statuses:", err);
      if (err.response?.status === 401) setError("You are not authorized. Please log in again.");
      else if (err.code === "ECONNABORTED") setError("Request timed out. Please try again.");
      else setError(pickApiMessage(err, "Failed to load room statuses. Please try again."));
    } finally {
      setLoading(false);
    }
  }, [hotelId]);

  useEffect(() => {
    if (hotelId) fetchRoomStatuses();
  }, [hotelId, fetchRoomStatuses]);

  const filteredRooms = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return rooms.filter((room) => {
      if (statusTab !== "all" && normalizeStatus(room?.status) !== statusTab) return false;
      if (!q) return true;
      return Object.values(room).some((value) => String(value ?? "").toLowerCase().includes(q));
    });
  }, [rooms, searchQuery, statusTab, normalizeStatus]);

  const statusCounts = useMemo(() => {
    const counts = {
      total: rooms.length,
      Available: 0,
      Occupied: 0,
      Cleaning: 0,
      Reserved: 0,
      Maintenance: 0,
      Dirty: 0,
      OutOfService: 0,
      Unknown: 0,
    };

    rooms.forEach((r) => {
      const s = normalizeStatus(r?.status);
      if (counts[s] != null) counts[s] += 1;
      else counts.Unknown += 1;
    });

    return counts;
  }, [rooms, normalizeStatus]);

  /**
   * IMPORTANT: we update using YOUR existing backend endpoint:
   * PUT /api/Rooms/UpdateStatus/{roomId}
   * body: { newStatus: <int enum>, reason?: <string> }
   */
  const putStatusChange = useCallback(
    async (room, nextStatus, reason) => {
      if (!room?.roomId || !nextStatus) return;

      const roomId = room.roomId;

      // Normalize UI status to enum
      const enumValue = STATUS_TO_ENUM[nextStatus];
      if (enumValue == null) {
        notify.error("Invalid status value.");
        return;
      }

      // For Maintenance / OutOfService require reason (UI policy)
      if ((nextStatus === "Maintenance" || nextStatus === "OutOfService") && !String(reason || "").trim()) {
        notify.error("Please provide a reason.");
        return;
      }

      try {
        setUpdating(true);

        const res = await axiosInstance.put(`/Rooms/UpdateStatus/${roomId}`, {
          newStatus: enumValue,
          reason: String(reason || "").trim() || null, // backend may ignore unless you add it to DTO
        });

        // Your controller returns NoContent() => HTTP 204
        if (res?.status === 204 || res?.status === 200) {
          notify.success({
            title: "Status Updated",
            description: `Room ${room.roomNumber ?? roomId} is now ${nextStatus}.`,
            placement: "topRight",
          });

          setStatusModalOpen(false);
          setSelectedRoom(null);
          setNewStatus(null);
          setMaintenanceReason("");

          await fetchRoomStatuses();
          return;
        }

        // Fallback
        notify.success({
          title: "Status Updated",
          description: `Room ${room.roomNumber ?? roomId} updated.`,
          placement: "topRight",
        });
        await fetchRoomStatuses();
      } catch (err) {
        console.error("Error updating room status:", err);

        const apiMsg = pickApiMessage(err, "Failed to update room status.");
        const status = err.response?.status;

        if (status === 400) {
          notify.warning({
            title: "Cannot Update Status",
            description: apiMsg,
            placement: "topRight",
            duration: 5,
          });
        } else if (status === 404) {
          notify.error({
            title: "Room Not Found",
            description: apiMsg,
            placement: "topRight",
          });
        } else if (status === 405) {
          notify.error({
            title: "Method Not Allowed (405)",
            description:
              "Your API route does not accept this HTTP method. Confirm the backend supports PUT /api/Rooms/UpdateStatus/{roomId}.",
            placement: "topRight",
            duration: 6,
          });
        } else {
          notify.error({
            title: "Update Failed",
            description: apiMsg,
            placement: "topRight",
          });
        }
      } finally {
        setUpdating(false);
      }
    },
    [fetchRoomStatuses]
  );

  const requestStatusChange = useCallback(
    (room, nextStatus) => {
      if (nextStatus === "Maintenance" || nextStatus === "OutOfService") {
        setSelectedRoom(room);
        setNewStatus(nextStatus);
        setMaintenanceReason("");
        setStatusModalOpen(true);
        return;
      }

      Modal.confirm({
        title: `Change status to ${nextStatus}?`,
        content: `Room ${room.roomNumber} will be set to ${nextStatus}.`,
        okText: "Confirm",
        cancelText: "Cancel",
        centered: true,
        onOk: () => putStatusChange(room, nextStatus),
      });
    },
    [putStatusChange]
  );

  const tabs = useMemo(
    () => [
      { key: "all", label: "All", count: statusCounts.total },
      { key: "Available", label: "Available", count: statusCounts.Available },
      { key: "Occupied", label: "Occupied", count: statusCounts.Occupied },
      { key: "Cleaning", label: "Cleaning", count: statusCounts.Cleaning },
      { key: "Reserved", label: "Reserved", count: statusCounts.Reserved },
      { key: "Maintenance", label: "Maintenance", count: statusCounts.Maintenance },
      { key: "Dirty", label: "Dirty", count: statusCounts.Dirty },
      { key: "OutOfService", label: "Out of service", count: statusCounts.OutOfService },
    ],
    [statusCounts]
  );

  const columns = useMemo(() => {
    return [
      {
        title: "Room",
        dataIndex: "roomNumber",
        key: "roomNumber",
        width: 140,
        sorter: (a, b) => String(a.roomNumber || "").localeCompare(String(b.roomNumber || "")),
        render: (v, r) => (
          <div className="hms-cell-stack">
            <span className="hms-cell-main">{v || "—"}</span>
            <span className="hms-cell-sub">#{r?.roomId}</span>
          </div>
        ),
      },
      {
        title: "Type",
        dataIndex: "roomTypeName",
        key: "roomTypeName",
        width: 200,
        sorter: (a, b) => String(a.roomTypeName || "").localeCompare(String(b.roomTypeName || "")),
        render: (v) => v || "—",
      },
      {
        title: "Floor",
        dataIndex: "floor",
        key: "floor",
        width: 110,
        sorter: (a, b) => String(a.floor || "").localeCompare(String(b.floor || "")),
        render: (v) => v ?? "—",
      },
      {
        title: "Check-out",
        dataIndex: "checkOutDate",
        key: "checkOutDate",
        width: 150,
        sorter: (a, b) => new Date(a.checkOutDate || 0) - new Date(b.checkOutDate || 0),
        render: (v) => (v ? fmtDate(v) : <span className="hms-muted">—</span>),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        width: 190,
        render: (status, record) => {
          const current = normalizeStatus(status);
          const transitions = getAvailableStatusTransitions(current);

          const items = transitions.map((s) => ({
            key: s,
            label: <StatusTag status={s}>{s === "OutOfService" ? "Out of service" : s}</StatusTag>,
          }));

          const clickable = items.length > 0;

          const chip = (
            <span className={`hms-status-select ${clickable ? "is-clickable" : ""}`}>
              <StatusTag status={current}>{current === "OutOfService" ? "Out of service" : current}</StatusTag>
              {clickable && <IconDown size={14} />}
            </span>
          );

          if (!clickable) return chip;

          return (
            <Dropdown
              trigger={["click"]}
              menu={{
                items,
                onClick: ({ key }) => requestStatusChange(record, String(key)),
              }}
            >
              {chip}
            </Dropdown>
          );
        },
      },
    ];
  }, [normalizeStatus, getAvailableStatusTransitions, requestStatusChange]);

  return (
    <div className="hms-page rooms-page">
      {error && <Alert style={{ marginBottom: 12 }} message="Could not load room statuses" description={error} type="error" showIcon />}

      <SectionCard
        flush
        title={<CardTitle count={statusCounts.total}>Room status</CardTitle>}
        extra={
          <ListToolbar
            search={{ value: searchQuery, onChange: setSearchQuery, placeholder: "Search rooms" }}
            onRefresh={fetchRoomStatuses}
            loading={loading}
          />
        }
      >
        <TabBar tabs={tabs} value={statusTab} onChange={setStatusTab} />
        <div className="hms-card-hint">Click a status to move the room to its next valid state.</div>

        <Table
          className="hms-table"
          columns={columns}
          dataSource={filteredRooms}
          rowKey="roomId"
          loading={loading}
          scroll={{ x: 900 }}
          pagination={tablePagination("room")}
          locale={{ emptyText: <div className="hms-empty">No rooms in this view</div> }}
        />
      </SectionCard>

      <Modal
        title={selectedRoom ? `Set room ${selectedRoom.roomNumber} to ${newStatus === "OutOfService" ? "out of service" : newStatus}` : "Set status"}
        open={statusModalOpen}
        confirmLoading={updating}
        okText="Update status"
        width={480}
        onOk={() => {
          if (!selectedRoom || !newStatus) return;
          putStatusChange(selectedRoom, newStatus, maintenanceReason);
        }}
        onCancel={() => {
          setStatusModalOpen(false);
          setSelectedRoom(null);
          setNewStatus(null);
          setMaintenanceReason("");
        }}
        destroyOnHidden
      >
        <p className="hms-modal-note">Please give a short reason. It will be logged.</p>
        <div className="hms-field">
          <label htmlFor="rs-reason">Reason</label>
          <Input.TextArea
            id="rs-reason"
            placeholder={`Why is this room going to ${newStatus === "OutOfService" ? "out of service" : newStatus || "this status"}?`}
            value={maintenanceReason}
            onChange={(e) => setMaintenanceReason(e.target.value)}
            rows={4}
          />
        </div>
      </Modal>
    </div>
  );
};

export default RoomStatusPage;
